import { API_BASE, METRIC_BUILDER_BASE } from '@/config/routes';
import type { CalibrationMetric } from '../lib/types';
import {
  mockCreateMetric,
  mockDeleteMetric,
  mockFetchAllMetrics,
  mockUpdateMetric,
} from '../mocks/mock-api';
import { getBotId, getUserId, isUsingMockApi, resolveUsers, throwIfNotOk } from './api';

// Re-exported under this file's existing public names — criteria-step.tsx
// imports both from here — so identity resolution has one implementation
// (api.ts's) instead of two copies that can drift on fallback order (this
// file's getCurrentUserId had already drifted from api.ts's getUserId in
// name, which is exactly how a behavioral divergence starts).
export { getBotId };
export { getUserId as getCurrentUserId };

// ─── Shared request helper (same header contract as lib/api.ts) ─────────────

async function request<T>(base: string, path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  headers.set('x-version', '2');
  const botId = getBotId();
  if (botId) headers.set('x-bot-id', botId);
  const userId = getUserId();
  if (userId) headers.set('x-user-id', userId);
  if (init.body) headers.set('content-type', 'application/json');

  const res = await fetch(`${base}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  await throwIfNotOk(res);

  // DELETE may return an empty body.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// Metric endpoints may wrap the result in `{ payload }` like the runs API.
function unwrap<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'payload' in (raw as Record<string, unknown>)) {
    const payload = (raw as Record<string, unknown>).payload;
    if (payload !== undefined && payload !== null) return payload as T;
  }
  return raw as T;
}

// ─── Metrics CRUD (observation-run resource) ─────────────────────────────────

type RawMetric = Omit<CalibrationMetric, 'updatedBy' | 'createdBy'> & {
  createdBy?: string;
  updatedBy?: string;
};

// Resolve `updatedBy`/`createdBy` raw ids into AISUser objects (mirrors the Vue
// store's resolveUserDetails), falling back to the raw id when unresolvable.
async function enrichMetricsWithUsers(metrics: RawMetric[]): Promise<CalibrationMetric[]> {
  const ids = new Set<string>();
  for (const m of metrics) {
    if (typeof m.updatedBy === 'string' && m.updatedBy) ids.add(m.updatedBy);
    if (typeof m.createdBy === 'string' && m.createdBy) ids.add(m.createdBy);
  }
  if (ids.size === 0) return metrics as CalibrationMetric[];
  let users: Awaited<ReturnType<typeof resolveUsers>>;
  try {
    users = await resolveUsers([...ids]);
  } catch {
    return metrics as CalibrationMetric[];
  }
  return metrics.map((m) => ({
    ...m,
    createdBy:
      typeof m.createdBy === 'string' ? (users.get(m.createdBy) ?? m.createdBy) : m.createdBy,
    updatedBy:
      typeof m.updatedBy === 'string' ? (users.get(m.updatedBy) ?? m.updatedBy) : m.updatedBy,
  }));
}

async function realFetchAllMetrics(signal?: AbortSignal): Promise<CalibrationMetric[]> {
  const raw = await request<unknown>(API_BASE, '/metrics', { signal });
  const list = unwrap<RawMetric[]>(raw);
  return enrichMetricsWithUsers(Array.isArray(list) ? list : []);
}

async function realCreateMetric(payload: Record<string, unknown>): Promise<CalibrationMetric> {
  const raw = await request<unknown>(API_BASE, '/metrics', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const created = unwrap<RawMetric>(raw);
  const [enriched] = await enrichMetricsWithUsers([created]);
  return enriched ?? (created as CalibrationMetric);
}

async function realUpdateMetric(
  id: string,
  payload: Record<string, unknown>
): Promise<CalibrationMetric> {
  const raw = await request<unknown>(API_BASE, `/metrics/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const updated = unwrap<RawMetric>(raw);
  const [enriched] = await enrichMetricsWithUsers([updated]);
  return enriched ?? (updated as CalibrationMetric);
}

async function realDeleteMetric(id: string): Promise<void> {
  await request<void>(API_BASE, `/metrics/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Dispatched like lib/api.ts's own functions — same isUsingMockApi() flag, so
// flipping it swaps both the runs list and the metrics library together.
export const fetchAllMetrics: typeof realFetchAllMetrics = (...args) =>
  (isUsingMockApi() ? mockFetchAllMetrics : realFetchAllMetrics)(...args);
export const createMetric: typeof realCreateMetric = (...args) =>
  (isUsingMockApi() ? mockCreateMetric : realCreateMetric)(...args);
export const updateMetric: typeof realUpdateMetric = (...args) =>
  (isUsingMockApi() ? mockUpdateMetric : realUpdateMetric)(...args);
export const deleteMetric: typeof realDeleteMetric = (...args) =>
  (isUsingMockApi() ? mockDeleteMetric : realDeleteMetric)(...args);

// ─── DS metric-builder pipeline ──────────────────────────────────────────────
//
// Not dispatched through isUsingMockApi() like the CRUD functions above — this
// is a stateful multi-step job (validate -> build -> schema-test -> result)
// against DS directly, not the observation-run CRUD surface a single mock
// response can stand in for. Always hits the real backend; unavailable in
// mock mode (the "Add Criterion" AI-generation flow will fail without
// VITE_BACKEND_URL, same as before this file had a mock layer at all).
//
// Mirrors aistudio's customMetrics.validateAndCreate: three POSTs map 1:1 onto
// the three UI progress stages, then GET /result/{sessionId} returns the
// generated rubric.
//   POST /metric-builder/validate    → { status, session_id, duplicate?, error? }
//   POST /metric-builder/build       → { status, session_id, error? }
//   POST /metric-builder/schema-test → { status, session_id, error? }
//   GET  /metric-builder/result/{id} → { metric_id, judge_system, judge_instruction, ... }

export const METRIC_OUTCOME = {
  SUCCESS: 'SUCCESS',
  SUCCESS_SIMILAR: 'SUCCESS_SIMILAR',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  SYSTEM_ERROR: 'SYSTEM_ERROR',
} as const;

export type MetricOutcome = (typeof METRIC_OUTCOME)[keyof typeof METRIC_OUTCOME];

export interface CustomMetricFormInput {
  name: string;
  description: string;
  goodBehavior?: string;
  badBehavior?: string;
  skipCriteriaWhen?: string;
  botId?: string | null;
  botRefId?: string | null;
  env?: string | null;
}

export interface PipelineResult {
  status: MetricOutcome;
  metric?: Record<string, unknown>;
  reason?: string;
  similarMetricId?: string;
  similarMetricName?: string;
  message?: string;
}

export interface ValidateJob {
  promise: Promise<PipelineResult>;
  onStage: (fn: (stage: number) => void) => void;
  cancel: () => void;
}

interface DsStepResponse {
  status?: string;
  session_id?: string;
  duplicate?: {
    detected?: boolean;
    matched_metric_id?: string;
    matched_metric_name?: string;
    message?: string;
  };
  error?: { message?: string };
}

interface DsResultResponse {
  metric_id?: string;
  metric_name?: string;
  metric_definition?: string;
  judge_system?: string;
  judge_instruction?: string;
}

// DS can send status in any casing (FAILED/Failed/failed) or omit it and only
// set error.message — matching the exact lowercase string missed those cases
// and let the pipeline continue into the next stage with a failed session,
// discarding the actionable error.message in favour of a generic banner.
function isDsStepFailed(step: DsStepResponse | null | undefined): boolean {
  if (!step) return false;
  if (step.error?.message) return true;
  return typeof step.status === 'string' && step.status.toLowerCase() === 'failed';
}

const cancelledError = () => Object.assign(new Error('cancelled'), { cancelled: true });

export function isCancelledError(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { cancelled?: boolean }).cancelled === true;
}

// Builds the POST /metrics request body from the form input + the DS-generated
// rubric. The server returns the authoritative record (id, timestamps, etc.).
function buildCustomMetricPayload(
  payload: CustomMetricFormInput,
  extra: {
    judgeInstruction?: string;
    judgeSystem?: string;
    metricKey?: string;
    metricDefinition?: string;
  }
): Record<string, unknown> {
  return {
    displayName: payload.name?.trim(),
    type: 'CUSTOM',
    category: 'quality_metrics',
    description: extra.metricDefinition || payload.description?.trim() || '',
    metadata: {
      goodBehavior: payload.goodBehavior?.trim() || '',
      badBehavior: payload.badBehavior?.trim() || '',
      skipCriteriaWhen: payload.skipCriteriaWhen?.trim() || '',
      judgeSystem: extra.judgeSystem || '',
      judgeInstruction: extra.judgeInstruction || '',
      metricDefinition: extra.metricDefinition,
    },
  };
}

async function runPipeline(
  payload: CustomMetricFormInput,
  emitStage: (i: number) => void,
  isCancelled: () => boolean
): Promise<PipelineResult> {
  const dsRequest = {
    bot_id: payload.botId || null,
    bot_ref_id: payload.botRefId || null,
    env: payload.env ? String(payload.env).toUpperCase() : null,
    name: payload.name?.trim(),
    definition: payload.description?.trim(),
    success_example: payload.goodBehavior?.trim() || '',
    failure_example: payload.badBehavior?.trim() || '',
    na_condition: payload.skipCriteriaWhen?.trim() || '',
  };

  try {
    // Stage 0 — validate. Every other call in this file unwraps a possible
    // `{ payload }` envelope (fetchAllMetrics/createMetric/updateMetric) —
    // these three DS steps and the result fetch below are the only calls
    // that didn't, so a wrapping proxy silently degraded session_id to
    // undefined and every downstream step failed with no actionable message.
    emitStage(0);
    const validate = unwrap<DsStepResponse>(
      await request<unknown>(METRIC_BUILDER_BASE, '/validate', {
        method: 'POST',
        body: JSON.stringify(dsRequest),
      })
    );
    if (isCancelled()) throw cancelledError();
    if (isDsStepFailed(validate)) {
      return { status: METRIC_OUTCOME.VALIDATION_FAILED, reason: validate?.error?.message };
    }
    const sessionId = validate?.session_id;
    const duplicate = validate?.duplicate;

    // Stage 1 — build
    emitStage(1);
    const build = unwrap<DsStepResponse>(
      await request<unknown>(METRIC_BUILDER_BASE, '/build', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      })
    );
    if (isCancelled()) throw cancelledError();
    if (isDsStepFailed(build)) {
      return { status: METRIC_OUTCOME.VALIDATION_FAILED, reason: build?.error?.message };
    }

    // Stage 2 — schema test
    emitStage(2);
    const schema = unwrap<DsStepResponse>(
      await request<unknown>(METRIC_BUILDER_BASE, '/schema-test', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      })
    );
    if (isCancelled()) throw cancelledError();
    if (isDsStepFailed(schema)) {
      return { status: METRIC_OUTCOME.VALIDATION_FAILED, reason: schema?.error?.message };
    }

    // Fetch the generated rubric and assemble the metric record.
    const result = unwrap<DsResultResponse>(
      await request<unknown>(METRIC_BUILDER_BASE, `/result/${encodeURIComponent(sessionId ?? '')}`)
    );
    if (isCancelled()) throw cancelledError();
    const metric = buildCustomMetricPayload(payload, {
      judgeInstruction: result?.judge_instruction,
      judgeSystem: result?.judge_system,
      metricKey: result?.metric_id,
      metricDefinition: result?.metric_definition,
    });

    if (duplicate?.detected) {
      return {
        status: METRIC_OUTCOME.SUCCESS_SIMILAR,
        metric,
        similarMetricId: duplicate.matched_metric_id,
        similarMetricName: duplicate.matched_metric_name,
        message: duplicate.message,
      };
    }
    return { status: METRIC_OUTCOME.SUCCESS, metric };
  } catch (err) {
    if (isCancelledError(err)) throw err;
    // Network / timeout / 5xx → system issue, never framed as a definition problem.
    return { status: METRIC_OUTCOME.SYSTEM_ERROR };
  }
}

// Runs the async validation + creation pipeline. Returns a job handle:
// { promise, onStage(fn), cancel() }. `cancel()` makes the job session-bound —
// the promise rejects with a cancelled error and nothing is created.
export function validateAndCreate(payload: CustomMetricFormInput): ValidateJob {
  let cancelled = false;
  const stageListeners: Array<(i: number) => void> = [];

  const promise = runPipeline(
    payload,
    (i) => {
      for (const fn of stageListeners) fn(i);
    },
    () => cancelled
  );

  return {
    promise,
    onStage(fn) {
      stageListeners.push(fn);
    },
    cancel() {
      cancelled = true;
    },
  };
}
